import { X, ChevronDown, ChevronUp, Plus, List } from 'lucide-react';
import { useState, useEffect, useContext } from 'react';
import { ButtonOption } from '../../types/mold';
import Modal from './Modal';
import { QuestionnaireIdPrefixContext } from '../Questionnaire/QuestionnaireIdPrefixContext';

type DisplayMode = 'inline' | 'modal';

interface MultiSelectorProps {
    options: ButtonOption[];
    label: string;
    labelForNoneSelected?: string;
    onSelect?: (selected: string[]) => void;
    value?: string[];
    hideLabel?: boolean; // Optional prop to hide the label
    previewMode?: boolean; // Optional prop to render in preview mode
    displayMode?: DisplayMode; // Optional prop to decide display mode
    // Support for custom values
    allowOther?: boolean; // Enable adding custom values via dedicated button
    onSaveCustomValue?: (value: string) => void; // Callback to save custom values to backend/options list
    customValuePlaceholder?: string; // Placeholder for custom input field
    // Per-field discriminator for option checkbox ids/htmlFor. Without it, `option-${index}`
    // collides across multiple multi_selector fields in one form; combined with the
    // context id-prefix it also stays unique across two co-mounted Questionnaires.
    idScope?: string;
}
const MultiSelector: React.FC<MultiSelectorProps> = ({
    options,
    label,
    labelForNoneSelected = 'ยังไม่มีเหตุผลที่เลือก',
    onSelect,
    value = [],
    hideLabel = false, // Optional prop to hide the label
    previewMode = false,
    displayMode = 'inline', // Default to inline mode
    allowOther = false,
    onSaveCustomValue,
    customValuePlaceholder = 'ระบุค่าที่ต้องการเพิ่ม',
    idScope = '',
}) => {
    const idPrefix = useContext(QuestionnaireIdPrefixContext);
    // Stable, collision-free base for each option's checkbox id + label htmlFor:
    // <instance-prefix><field-key>-option-<index>. Empty prefixes (standalone use)
    // degrade to the previous "option-<index>" shape.
    const optionDomId = (index: number) => `${idPrefix}${idScope ? `${idScope}-` : ''}option-${index}`;
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOptions, setSelectedOptions] = useState<ButtonOption[]>([]);
    const [isSelectedOptionsExpanded, setIsSelectedOptionsExpanded] = useState(false);
    const [editingCustomValue, setEditingCustomValue] = useState<string | null>(null);
    const [customText, setCustomText] = useState('');

    // Sync internal selected ButtonOption objects from external string `value`
    useEffect(() => {
        const valueStrings = (value || []).map(String);
        const selected: ButtonOption[] = [];

        valueStrings.forEach(val => {
            // First, try to find in existing options
            const existingOption = options.find(o => String(o.value) === val);
            if (existingOption) {
                selected.push(existingOption);
            } else {
                // This is a custom value not in the options list
                // Create a ButtonOption for it
                selected.push({
                    value: val,
                    label: `อื่นๆ: ${val}`,
                    isCustom: true,
                    bgColor: 'bg-purple-100',
                    textColor: 'text-purple-700',
                });
            }
        });

        setSelectedOptions(selected);
    }, [options, value]);

    // Clear search when dropdown closes
    useEffect(() => {
        if (!isDropdownOpen) {
            setSearchTerm('');
            setIsSelectedOptionsExpanded(false); // Reset expansion when modal closes
        }
    }, [isDropdownOpen]);

    const displayedOptions = (searchTerm || '').trim() === ''
        ? options
        : options.filter(o => {
            const q = searchTerm.trim().toLowerCase();
            return String(o.label).toLowerCase().includes(q) || (o.group && String(o.group).toLowerCase().includes(q));
        });

    const deselectOption = (option: ButtonOption) => {
        const newSelectedOptions = selectedOptions.filter(r => String(r.value) !== String(option.value));
        setSelectedOptions(newSelectedOptions);
        onSelect?.(newSelectedOptions.map(o => String(o.value)));
    };
    const isSelectedOption = (option: ButtonOption) => {
        return selectedOptions.some(o => String(o.value) === String(option.value));
    }
    const selectOption = (option: ButtonOption) => {
        if (isSelectedOption(option)) return;

        const newSelectedOptions = [...selectedOptions, option];
        setSelectedOptions(newSelectedOptions);
        onSelect?.(newSelectedOptions.map(o => String(o.value)));
    };
    
    const handleAddCustomValue = () => {
        setEditingCustomValue('new');
        setCustomText('');
    };

    const handleSaveCustomValue = () => {
        if (!customText.trim()) {
            setEditingCustomValue(null);
            return;
        }

        const trimmedText = customText.trim();

        // Check if this custom value already exists
        const alreadyExists = selectedOptions.some(o => String(o.value) === trimmedText);
        if (alreadyExists) {
            setEditingCustomValue(null);
            setCustomText('');
            return;
        }

        // Optionally save to backend/options list
        if (onSaveCustomValue) {
            onSaveCustomValue(trimmedText);
        }

        // Add as selected value
        const customOption: ButtonOption = {
            value: trimmedText,
            label: trimmedText,
            isCustom: true,
            bgColor: 'bg-purple-100',
            textColor: 'text-purple-700',
        };

        const newSelectedOptions = [...selectedOptions, customOption];
        setSelectedOptions(newSelectedOptions);
        onSelect?.(newSelectedOptions.map(o => String(o.value)));

        setEditingCustomValue(null);
        setCustomText('');
    };

    // Render the selected options display (used in both main display and modal)
    const renderSelectedOptions = (isInModal = false) => {
        const hasMultipleOptions = selectedOptions.length > 3;
        const shouldShowExpansion = isInModal && hasMultipleOptions;
        const maxHeightClass = isInModal
            ? (isSelectedOptionsExpanded ? 'max-h-48' : 'max-h-20')
            : '';
        const containerClass = isInModal
            ? `flex flex-wrap gap-2 ${maxHeightClass} overflow-y-auto transition-all duration-300`
            : 'flex flex-wrap gap-2';

        return (
            <div>
                <div className={containerClass}>
                    {/* {selectedOptions.length === 0 && (
                        <span className="text-gray-400 py-1 text-sm italic">{labelForNoneSelected}</span>
                    )} */}

                    {selectedOptions.map((option) => {
                        const bgColor = option.bgColor ?? 'bg-gray-100';
                        const textColor = option.textColor ?? 'text-gray-700';
                        const className = `${bgColor} ${textColor} text-sm rounded-full px-3 py-1 flex items-center`;
                        return (
                            <span
                                key={String(option.value)}
                                className={className}
                            >
                                {option.label}
                                {!previewMode && <button
                                    onClick={(e) => { e.stopPropagation(); deselectOption(option); }}
                                    className={`ml-1.5 ${textColor} hover:text-red-700`}
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>}
                            </span>
                        )
                    })}
                </div>

                {/* Expand/Collapse button for modal */}
                {shouldShowExpansion && (
                    <button
                        onClick={() => setIsSelectedOptionsExpanded(!isSelectedOptionsExpanded)}
                        className="mt-2 flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
                    >
                        {isSelectedOptionsExpanded ? (
                            <>
                                <ChevronUp className="h-4 w-4 mr-1" />
                                ย่อรายการ
                            </>
                        ) : (
                            <>
                                <ChevronDown className="h-4 w-4 mr-1" />
                                ดูทั้งหมด ({selectedOptions.length} รายการ)
                            </>
                        )}
                    </button>
                )}
            </div>
        );
    };

    // Render the dropdown content (used in both inline and modal modes)
    const renderDropdownContent = () => (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-2 max-h-96 overflow-y-auto">
            {/* Search input - sticky so user can type while scrolling options */}
            <div className="px-2 py-2 sticky top-0 bg-white z-10">
                <input
                    type="text"
                    placeholder="ค้นหา..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full border border-gray-200 rounded px-2 py-2 text-sm"
                />
            </div>
            {/* Options list or no-results message */}
            <div className="grid grid-cols-1 divide-y divide-gray-100">
                {displayedOptions.length === 0 && (searchTerm.trim() !== '') ? (
                    <div className="px-3 py-4 text-sm text-gray-500">ไม่พบตัวเลือกที่ตรงกับการค้นหา "{searchTerm}"</div>
                ) : (
                    displayedOptions.map((option: ButtonOption, index: number) => {
                        return (
                            <div
                                key={index}
                                role="button"
                                tabIndex={0}
                                className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                onClick={(event) => {
                                    // Ignore clicks that originated from the native input/label
                                    // so those elements can handle the interaction themselves.
                                    const target = event.target as HTMLElement;
                                    if (target.closest('input, label')) return;

                                    if (isSelectedOption(option)) {
                                        deselectOption(option);
                                    } else {
                                        selectOption(option);
                                    }
                                }}
                            >
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id={optionDomId(index)}
                                        checked={isSelectedOption(option)}
                                        className="h-4 w-4 text-red-600 rounded"
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            if (isSelectedOption(option)) {
                                                deselectOption(option);
                                            } else {
                                                selectOption(option);
                                            }
                                        }}
                                    />
                                    <label
                                        htmlFor={optionDomId(index)}
                                        className="ml-2 block text-sm text-gray-700 cursor-pointer"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {option.label}
                                    </label>
                                </div>

                                {/* Group label chip on the right */}
                                {option.group && (
                                    <span
                                        className={`ml-4 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${isSelectedOption(option)
                                            ? option.selectedBgColor ?? option.bgColor ?? 'bg-gray-100'
                                            : option.bgColor ?? 'bg-gray-100'
                                            } ${option.textColor ?? 'text-gray-700'}`}
                                    >
                                        {String(option.group)}
                                    </span>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
            
            {/* Add Custom Value Button */}
            {allowOther && !editingCustomValue && (
                <div className="p-3 border-t border-gray-200">
                    <button
                        onClick={handleAddCustomValue}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-purple-700 bg-purple-50 border-2 border-purple-300 border-dashed rounded-lg hover:bg-purple-100 hover:border-purple-400 transition-colors"
                        type="button"
                    >
                        <Plus className="h-4 w-4" />
                        <span>เพิ่มค่าที่ต้องการเอง</span>
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <div className="w-full mb-4">
            {<div className="mb-3">
                {!hideLabel && <label className="text-sm font-medium text-gray-700 mb-2 block">{label}:</label>}

                {/* Selected Reasons Display - Before Button */}
                <div className="mb-3">
                    {renderSelectedOptions()}
                </div>

                {/* Redesigned Toggle Button - Enhanced Visual Appeal */}
                {!previewMode && <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`
                        group relative inline-flex items-center justify-center gap-2 px-4 py-2.5 
                        text-sm font-medium rounded-lg border-2 transition-all duration-300 ease-out
                        focus:outline-none focus:ring-4 focus:ring-offset-2 transform hover:scale-[1.01] active:scale-[0.99]
                        ${isDropdownOpen
                            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white border-blue-600 hover:from-blue-700 hover:to-blue-800 shadow-xl focus:ring-blue-500/50'
                            : selectedOptions.length === 0
                                ? 'bg-gradient-to-r from-gray-50 to-white text-gray-700 border-gray-300 hover:from-blue-50 hover:to-blue-100 hover:border-blue-300 hover:text-blue-700 shadow-lg hover:shadow-xl focus:ring-blue-500/30'
                                : 'bg-gradient-to-r from-indigo-50 to-indigo-100 text-indigo-800 border-indigo-300 hover:from-indigo-100 hover:to-indigo-200 hover:border-indigo-400 shadow-lg hover:shadow-xl focus:ring-indigo-500/30'
                        }
                        ${selectedOptions.length === 0 ? 'border-dashed' : 'border-solid'}
                        backdrop-blur-sm
                    `}
                    type="button"
                    aria-expanded={isDropdownOpen}
                    aria-label={isDropdownOpen ? 'ปิดรายการตัวเลือก' : 'เปิดรายการตัวเลือก'}
                >
                    {/* Background Glow Effect */}
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-600/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-lg -z-10" />

                    {/* Icon with Enhanced Styling */}
                    <div className={`
                        flex items-center justify-center w-4 h-4 rounded-full transition-all duration-300
                        ${isDropdownOpen
                            ? 'bg-white/20 text-white'
                            : selectedOptions.length === 0
                                ? 'bg-blue-100 text-blue-600 group-hover:bg-blue-200'
                                : 'bg-indigo-200 text-indigo-700 group-hover:bg-indigo-300'
                        }
                    `}>
                        {isDropdownOpen ? (
                            <ChevronUp className="h-3 w-3" />
                        ) : (
                            selectedOptions.length === 0 ? (
                                <Plus className="h-3 w-3" />
                            ) : (
                                <List className="h-3 w-3" />
                            )
                        )}
                    </div>

                    {/* Button Text with Enhanced Typography */}
                    <span className="relative font-medium">
                        {isDropdownOpen
                            ? 'ปิดรายการ'
                            : selectedOptions.length === 0
                                ? 'เลือกตัวเลือก'
                                : `แก้ไขรายการ (${selectedOptions.length})`
                        }
                    </span>

                    {/* Dropdown Indicator with Animation */}
                    {!isDropdownOpen && (
                        <div className="flex items-center justify-center">
                            <ChevronDown className={`
                                h-3.5 w-3.5 transition-all duration-300 group-hover:translate-y-0.5
                                ${selectedOptions.length === 0 ? 'text-gray-400' : 'text-indigo-600'}
                            `} />
                        </div>
                    )}

                    {/* Shine Effect */}
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-500" />
                </button>}
            </div>}

            {/* Compact Reason Selection Dropdown - Inline Mode */}
            {!previewMode && isDropdownOpen && displayMode === 'inline' && (
                <div className="max-h-40 overflow-y-auto">
                    {/* Custom Value Input - shown when editing custom value */}
                    {editingCustomValue && (
                        <div className="mb-2 p-3 bg-blue-50 rounded-lg border-2 border-blue-300">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={customText}
                                    onChange={(e) => setCustomText(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSaveCustomValue();
                                        }
                                    }}
                                    className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder={customValuePlaceholder}
                                    autoFocus
                                />
                                <button
                                    onClick={handleSaveCustomValue}
                                    className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium"
                                >
                                    บันทึก
                                </button>
                                <button
                                    onClick={() => {
                                        setEditingCustomValue(null);
                                        setCustomText('');
                                    }}
                                    className="px-3 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors text-sm font-medium"
                                >
                                    ยกเลิก
                                </button>
                            </div>
                        </div>
                    )}
                    {renderDropdownContent()}
                </div>
            )}

            {/* Modal Mode */}
            {!previewMode && isDropdownOpen && displayMode === 'modal' && (
                <Modal
                    onClose={() => {
                        setIsDropdownOpen(false);
                        setEditingCustomValue(null);
                        setCustomText('');
                    }}
                    maxWidth="max-w-2xl"
                    isBackgroundTransparent={true}
                    testId="multi-selector-modal"
                >
                    <div className="pb-4">
                        <h3 className="text-lg font-semibold mb-4 text-gray-800">{label}</h3>

                        {/* Selected Options Display in Modal */}
                        <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">ตัวเลือกที่เลือกแล้ว:</h4>
                            {renderSelectedOptions(true)}
                        </div>

                        {/* Custom Value Input - shown when editing custom value */}
                        {editingCustomValue && (
                            <div className="mb-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">ระบุค่าที่ต้องการเพิ่ม:</h4>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={customText}
                                        onChange={(e) => setCustomText(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleSaveCustomValue();
                                            }
                                        }}
                                        className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder={customValuePlaceholder}
                                        autoFocus
                                    />
                                    <button
                                        onClick={handleSaveCustomValue}
                                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium"
                                    >
                                        บันทึก
                                    </button>
                                    <button
                                        onClick={() => {
                                            setEditingCustomValue(null);
                                            setCustomText('');
                                        }}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors text-sm font-medium"
                                    >
                                        ยกเลิก
                                    </button>
                                </div>
                            </div>
                        )}

                        {renderDropdownContent()}
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default MultiSelector;